// Footer row showing live-computed totals per column. Recomputes on
// every render — totals over even a few thousand rows are sub-ms.
//
// Aggregate kind: simple SUM/AVG/MIN/MAX/COUNT applied to the column.
// Expression kind: evaluated via expressionEval over the visible rows.

import { AlertTriangle } from 'lucide-react'

import type { ColumnDef } from '../types'
import type { EmbedColumnTotal } from '@twasta/page-schema'
import { applyAggregate, evaluateExpression } from './expressionEval'

interface Props {
  columns: ColumnDef[]
  columnKeyOf: (c: ColumnDef) => string
  columnWidthOf: (c: ColumnDef) => number
  rows: Array<Record<string, unknown>>
  totals: Record<string, EmbedColumnTotal>
  // Leading filler cells to align with row decorations rendered by the
  // grid (status badge, line number, expand toggle). Provide the
  // pre-rendered <td> elements.
  leadingCells?: React.ReactNode
  // Trailing filler cell (e.g. actions column).
  trailingCell?: React.ReactNode
  // Override the footer label that prefixes the first totals cell.
  // Defaults to "Total".
  defaultLabel?: string
}

function formatNumber(n: number, fractionDigits?: number): string {
  if (!Number.isFinite(n)) return '—'
  const fd = fractionDigits ?? (Number.isInteger(n) ? 0 : 2)
  return n.toLocaleString(undefined, {
    minimumFractionDigits: fd,
    maximumFractionDigits: fd,
  })
}

interface FooterCellState {
  label: string
  display: string
  error?: string
}

function computeCell(
  spec: EmbedColumnTotal,
  column: string,
  rows: Array<Record<string, unknown>>,
  columnDbNames: string[],
  defaultLabel: string,
): FooterCellState {
  const label = spec.label ?? defaultLabel
  if (spec.kind === 'aggregate') {
    const agg = (spec.spec || 'sum').toLowerCase()
    if (!['sum', 'avg', 'min', 'max', 'count'].includes(agg)) {
      return { label, display: '—', error: `Unknown aggregate "${spec.spec}"` }
    }
    const v = applyAggregate(rows, column, agg as 'sum' | 'avg' | 'min' | 'max' | 'count')
    return { label, display: formatNumber(v) }
  }
  // expression
  const res = evaluateExpression(spec.spec, rows, columnDbNames)
  if (!res.ok) {
    return { label, display: '#ERR', error: res.error || 'Evaluation failed' }
  }
  const v = res.result
  if (typeof v === 'number') return { label, display: formatNumber(v) }
  if (typeof v === 'boolean') return { label, display: v ? 'TRUE' : 'FALSE' }
  return { label, display: String(v ?? '—') }
}

export default function ColumnTotalsRow({
  columns, columnKeyOf, columnWidthOf, rows, totals, leadingCells, trailingCell,
  defaultLabel = 'Total',
}: Props) {
  // Bail out if nothing to show.
  const hasAny = Object.values(totals).some(t => !!t)
  if (!hasAny) return null

  const columnDbNames = columns.map(columnKeyOf)
  // Find the first column with a total — its cell gets the label prefix
  // when no per-column label is set, so the user sees what "Total" means.
  const firstWithTotal = columns.find(c => totals[columnKeyOf(c)])

  return (
    <tr style={{
      borderTop: '2px solid var(--color-border)',
      background: 'var(--color-surface-2, var(--color-bg-secondary))',
      fontWeight: 600,
      position: 'sticky', bottom: 0, zIndex: 1,
    }}>
      {leadingCells}
      {columns.map((col) => {
        const key = columnKeyOf(col)
        const spec = totals[key]
        const width = columnWidthOf(col)
        if (!spec) {
          return (
            <td key={col.col_id || col.name}
                style={{ ...tdBase, width }}>
              {col === firstWithTotal ? null : null}
            </td>
          )
        }
        const cell = computeCell(spec, key, rows, columnDbNames, defaultLabel)
        return (
          <td
            key={col.col_id || col.name}
            style={{ ...tdBase, width, textAlign: 'right' }}
            title={cell.error || `${cell.label}: ${cell.display}`}
          >
            <div style={{
              display: 'flex', alignItems: 'center', justifyContent: 'flex-end',
              gap: 4,
            }}>
              <span style={{
                color: 'var(--color-text-secondary)',
                fontSize: '0.8125rem', fontWeight: 500,
                textTransform: 'uppercase', letterSpacing: '0.04em',
              }}>
                {cell.label}
              </span>
              <span style={{
                fontFamily: 'var(--font-mono, monospace)',
                color: cell.error ? '#ef4444' : 'var(--color-text)',
              }}>
                {cell.display}
              </span>
              {cell.error && (
                <AlertTriangle size={11} color="#ef4444" />
              )}
            </div>
          </td>
        )
      })}
      {trailingCell}
    </tr>
  )
}

const tdBase: React.CSSProperties = {
  padding: '6px 12px',
  fontSize: '0.9125rem',
}
